import {
  CheckCircleOutlined,
  ErrorOutlined,
  HelpOutlined,
  InfoOutlined,
  WarningAmber,
} from '@mui/icons-material'
import { Alert, AlertTitle, alpha, Box, Stack, Typography, useTheme } from '@mui/material'
import React, { FunctionComponent, ReactNode } from 'react'

import { StyledPaletteColors } from '../../../utils/getColor.ts'

interface OwnProps {
  title: string
  description?: string | ReactNode
  type?: StyledPaletteColors
  // Acepta Array de strings o de Componentes
  steps?: (string | ReactNode)[]
}

type Props = OwnProps

// Mapa de iconos por tipo (puedes sobrescribirlos si lo deseas)
const iconMap: Record<string, ReactNode> = {
  success: <CheckCircleOutlined sx={{ fontSize: 32 }} />,
  warning: <WarningAmber sx={{ fontSize: 32 }} />,
  error: <ErrorOutlined sx={{ fontSize: 32 }} />,
  info: <InfoOutlined sx={{ fontSize: 32 }} />,
}

/**
 * Componente que te ayuda a renderizar los mensajes de confirmacion
 * - Generalmente usado en operaciones de confirmacion con useConfirm
 *     const { confirmed } = await confirm({
 *       content: (
 *         <ActionConfirmMessage
 *           title={'Iniciar proceso de producción'}
 *           type={'success'}
 *           description={'Esta a punto de iniciar el proceso de producción'}
 *           steps={[
 *             'Se comprometen todos los insumos en inventario.',
 *             'Producto a procesar y productos adicionales no sufren cambios de estado en el inventario.',
 *           ]}
 *         />
 *       ),
 *     })
 * @param props
 * @constructor
 */
export const ContentConfirmMessage: FunctionComponent<Props> = (props) => {
  const { title, description, type = 'primary', steps = [] } = props
  const theme = useTheme()

  const mainColor = (theme.palette as any)[type]?.main || theme.palette.primary.main
  const icon = iconMap[type] || <HelpOutlined sx={{ fontSize: 32 }} />

  return (
    <Stack spacing={2}>
      <Alert
        severity={type as any}
        icon={icon}
        sx={{
          '& .MuiAlert-icon': {
            color: mainColor,
          },
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <AlertTitle sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: 0.4 }}>{title}</AlertTitle>
        {description && (
          <>
            {typeof description === 'string' ? (
              <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.4 }}>
                {description}
              </Typography>
            ) : (
              <Box sx={{ width: '100%', mt: -0.2 }}>{description}</Box>
            )}
          </>
        )}
      </Alert>

      {/* BLOQUE DE OPERACIONES: Fuera del Alert para limpieza visual */}
      {steps.length > 0 && (
        <Box
          sx={{
            width: '100%',
            px: 2.5,
            py: 1.5,
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.2),
            borderRadius: 2,
            border: '1px dashed',
            borderColor: (theme) => alpha(theme.palette.grey.A400, 0.6),
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.3, mt: -1, fontWeight: 700 }}
          >
            Operaciones:
          </Typography>
          <Stack spacing={1.5}>
            {steps.map((step, index) => (
              <Stack key={index} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                {/* El Bullet siempre presente para consistencia */}
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'success.light',
                    mt: '7px !important',
                    flexShrink: 0,
                  }}
                />

                {/* RENDERIZADO CONDICIONAL: Texto o Componente */}
                {typeof step === 'string' ? (
                  <Typography variant="body2" sx={{ lineHeight: 1.4, fontWeight: 500 }}>
                    {step}
                  </Typography>
                ) : (
                  // Si es un componente, lo renderizamos directamente
                  <Box sx={{ width: '100%', mt: -0.2 }}>{step}</Box>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}
